import { useCalendarState as originalUseCalendarState } from 'react-stately/useCalendarState';
import type {
  CalendarStateOptions as OriginalCalendarStateOptions,
  CalendarState as OriginalCalendarState,
} from 'react-stately/useCalendarState';
import { DateDuration, DateValue } from '@internationalized/date';
import { useRef } from 'react';
import { useControlledState } from 'react-stately/useControlledState';
import { ValueBasePropsKeys, CalendarStateKeys } from '../../utils/types';

type OriginalSetValue = OriginalCalendarState['setValue'];

type OriginalOnChange<T extends DateValue> = NonNullable<
  OriginalCalendarStateOptions<T>['onChange']
>;

export type CalendarView = 'days' | 'months' | 'years';
export type CalendarGranularity = 'day' | 'month' | 'year';

const viewToVisibleDuration: Record<CalendarView, DateDuration> = {
  days: {
    months: 1,
  },
  months: {
    years: 1,
  },
  years: {
    years: 12,
  },
};

// Map granularity to allowed views
const allowedViews: Record<CalendarGranularity, CalendarView[]> = {
  day: ['days', 'months', 'years'],
  month: ['months', 'years'],
  year: ['years'],
};

const defaultGranularityToView: Record<CalendarGranularity, CalendarView> = {
  day: 'days',
  month: 'months',
  year: 'years',
};

export type CalendarStateOptions<T extends DateValue, P extends string> = Pick<
  OriginalCalendarStateOptions<T>,
  | ValueBasePropsKeys
  | 'locale'
  | 'createCalendar'
  | 'selectionAlignment'
  | 'defaultFocusedValue'
> & {
  /**
   * Called whenever the value changes.
   * Second parameter is present if the selected date was predefined.
   */
  onChange?: (
    value: Parameters<OriginalOnChange<T>>[0],
    predefinedDate?: P
  ) => ReturnType<OriginalOnChange<T>>;

  /**
   * Controls the calendar's view (day, month, year).
   */
  view?: CalendarView;

  /**
   * The view of the calendar when it first mounts (uncontrolled).
   */
  defaultView?: CalendarView;
  /**
   * Handler that is called when the view is changed.
   */
  onViewChange?: (value: CalendarView) => void;

  granularity?: CalendarGranularity;
};

export type CalendarState<P extends string> = Pick<
  OriginalCalendarState,
  CalendarStateKeys
> & {
  setValue: (
    value: Parameters<OriginalSetValue>[0],
    predefinedDate?: P
  ) => ReturnType<OriginalSetValue>;
  view: CalendarView;
  narrowView: () => void;
  broadenView: () => void;
  canBroadenView: boolean;
  granularity: CalendarGranularity;
};

const getValidView = (
  view: CalendarView,
  granularity: CalendarGranularity
): CalendarView =>
  allowedViews[granularity].includes(view)
    ? view
    : defaultGranularityToView[granularity];

export const useCalendarState = <P extends string>({
  onChange,
  granularity = 'day',
  view: viewProp,
  defaultView,
  onViewChange,
  ...options
}: CalendarStateOptions<DateValue, P>): CalendarState<P> => {
  const initialView = getValidView(
    defaultView ?? defaultGranularityToView[granularity],
    granularity
  );

  const [view, setView] = useControlledState<CalendarView>(
    viewProp,
    initialView,
    onViewChange
  );
  const lastPredefinedDate = useRef<P | undefined>(undefined);

  const state = originalUseCalendarState({
    ...options,
    onChange: (value) => {
      onChange?.(value, lastPredefinedDate.current);
      lastPredefinedDate.current = undefined;
    },
    visibleDuration: viewToVisibleDuration[view],
  });

  const broadenView = () => {
    switch (view) {
      case 'days':
        return setView('months');
      case 'months':
        return setView('years');
    }
  };

  const narrowView = () => {
    let newView = view;
    switch (view) {
      case 'months':
        newView = 'days';
        break;
      case 'years':
        newView = 'months';
        break;
    }

    if (allowedViews[granularity].includes(newView)) {
      setView(newView);
    }
  };
  const canDrillUp = !state.isReadOnly && ['days', 'months'].includes(view);

  const effectiveView = getValidView(view, granularity);

  return {
    ...state,
    setValue: (value, predefinedDate) => {
      lastPredefinedDate.current = predefinedDate;
      state.setValue(value);
    },
    view: effectiveView,
    narrowView,
    broadenView,
    canBroadenView: canDrillUp,
    granularity,
  };
};
